import {
  IJobSpeciality,
  JobSearchGrade,
  JobSearchSpeciality,
  JobSearchWebCriteria,
  WorkType,
} from '@/app/types/jobs';
import { setScrollInstantOnFilterChangeRerender } from '@/app/utils/jobSearchAutoScroll';
import { useAnalytics } from '@/app/utils/useAnalytics';

import { Grid, MenuItem, Select, Typography } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';

import styles from './ResultsHeader.module.scss';

type ResultsHeaderProps = {
  readonly jobSpecialitiesMapped: Record<string, IJobSpeciality>;
  readonly query: JobSearchWebCriteria;
  readonly total: number;
  readonly isNonSchedulerLocationSelected: boolean;
  readonly mobile: boolean;
  readonly radiusInKm: string;
  readonly sortByClick: (type: string) => void;
  readonly trackingDetails: {
    logged_in: boolean;
    grade: JobSearchGrade | undefined;
    speciality: JobSearchSpeciality | undefined;
    work_type: WorkType | undefined;
    start_date: string | null | undefined;
    end_date: string | null | undefined;
    keywords: string | undefined;
    location: string | undefined;
  };
};

const getJobResultText = (query: JobSearchWebCriteria, total: number) => {
  const jobsText = `job${total !== 1 ? 's' : ''}`;
  if (query.grade && !query.speciality) {
    return `${total} ${query.grade} ${jobsText} found`;
  }
  if (query.grade && query.speciality) {
    return `${total} ${query.speciality} ${query.grade} ${jobsText} found`;
  }

  return `${total} ${jobsText} found`;
};

function ResultsHeader({
  jobSpecialitiesMapped,
  query,
  total,
  isNonSchedulerLocationSelected,
  mobile,
  radiusInKm,
  sortByClick,
  trackingDetails,
}: ResultsHeaderProps) {
  const track = useAnalytics();

  return (
    <Grid className={styles.resultsHeader} container direction="row" justifyContent="space-between">
      {jobSpecialitiesMapped && query.speciality && jobSpecialitiesMapped[query.speciality] && (
        <Grid item xs={12} className={styles.specialtyMessageWrapper}>
          <Typography variant="body1">
            {jobSpecialitiesMapped[query.speciality].description}
          </Typography>
        </Grid>
      )}
      <Grid item xs={8} className={styles.resultMessageWrapper}>
        <h1 className={styles.totalHeader} data-testid="job-search-results-message">
          {getJobResultText(query, total)}
        </h1>
        {isNonSchedulerLocationSelected && (
          <p className={styles.radius} data-testid="job-search-results-radius-message">
            {!mobile
              ? `Within a ${radiusInKm} km radius of the selected location`
              : `Within ${radiusInKm} km radius`}
          </p>
        )}
      </Grid>
      <Grid item xs={4}>
        <div className={styles.sortby}>
          <Select
            displayEmpty
            IconComponent={ArrowBackIosIcon}
            value={query.sortBy ?? ''}
            onChange={e => {
              setScrollInstantOnFilterChangeRerender();
              sortByClick(e.target.value as string);
              track('Job results Sort By', {
                sort_by: e.target.value as string,
                ...trackingDetails,
              });
            }}
          >
            <MenuItem value="">Sort By</MenuItem>
            <MenuItem value={'MostRecent'}>Recently added</MenuItem>
            <MenuItem value={'HighestPaid'}>Highest paid</MenuItem>
          </Select>
        </div>
      </Grid>
    </Grid>
  );
}

export default ResultsHeader;
